(() => {
  const STORAGE_KEY = "nelpanda:newsletter-offer";
  const SUBMITTED_STATUS = "submitted_pending";
  const STATE_TTL = 180 * 24 * 60 * 60 * 1000;
  const OPEN_TIMEOUT = 8000;
  const STATE_EVENT = "nelpanda:newsletter-offer-state";
  const ORDER_DISCOUNT_EVENT = "nelpanda:order-discount";
  let memoryState = null;
  let activeOffer = null;

  if (customElements.get("m-newsletter-offer")) return;

  class MNewsletterOffer extends HTMLElement {
    constructor() {
      super();
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleBadgeAnimationEnd = this.handleBadgeAnimationEnd.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleDialogCloseClick = this.handleDialogCloseClick.bind(this);
      this.handleKlaviyoEvent = this.handleKlaviyoEvent.bind(this);
      this.handleOrderDiscount = this.handleOrderDiscount.bind(this);
      this.handleStateChange = this.handleStateChange.bind(this);
      this.handleStorage = this.handleStorage.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector("[data-newsletter-offer-trigger]");
      this.badge = this.querySelector("[data-newsletter-offer-badge]");
      this.tooltip = this.querySelector("[data-newsletter-offer-tooltip]");
      this.dialog = this.querySelector("[data-newsletter-offer-dialog]");
      this.dialogClose = this.querySelector("[data-newsletter-offer-dialog-close]");
      this.dialogTitle = this.querySelector("[data-newsletter-offer-dialog-title]");
      this.dialogMessage = this.querySelector("[data-newsletter-offer-dialog-message]");

      this.trigger?.addEventListener("click", this.handleTriggerClick);
      this.badge?.addEventListener("animationend", this.handleBadgeAnimationEnd);
      this.dialog?.addEventListener("click", this.handleDialogClick);
      this.dialog?.addEventListener("close", this.handleDialogClose);
      this.dialogClose?.addEventListener("click", this.handleDialogCloseClick);
      window.addEventListener("klaviyoForms", this.handleKlaviyoEvent);
      document.addEventListener(ORDER_DISCOUNT_EVENT, this.handleOrderDiscount);
      window.addEventListener(STATE_EVENT, this.handleStateChange);
      window.addEventListener("storage", this.handleStorage);
      this.activeDiscount = Boolean(
        window.NelpandaFinalPrice?.orderDiscount?.active
        && Number(window.NelpandaFinalPrice.orderDiscount.percentage) === 10
      );
      this.syncState();
    }

    disconnectedCallback() {
      this.clearOpenTimeout();
      this.trigger?.removeEventListener("click", this.handleTriggerClick);
      this.badge?.removeEventListener("animationend", this.handleBadgeAnimationEnd);
      this.dialog?.removeEventListener("click", this.handleDialogClick);
      this.dialog?.removeEventListener("close", this.handleDialogClose);
      this.dialogClose?.removeEventListener("click", this.handleDialogCloseClick);
      window.removeEventListener("klaviyoForms", this.handleKlaviyoEvent);
      document.removeEventListener(ORDER_DISCOUNT_EVENT, this.handleOrderDiscount);
      window.removeEventListener(STATE_EVENT, this.handleStateChange);
      window.removeEventListener("storage", this.handleStorage);
    }

    get formId() {
      return this.dataset.formId?.trim() || "";
    }

    get offerVersion() {
      return this.dataset.offerVersion?.trim() || this.formId;
    }

    get discountFormId() {
      return this.dataset.activeFormId?.trim() || "";
    }

    get currentFormId() {
      return this.activeDiscount ? this.discountFormId : this.formId;
    }

    get currentOfferVersion() {
      return this.activeDiscount
        ? this.dataset.activeOfferVersion?.trim() || this.discountFormId
        : this.offerVersion;
    }

    readState() {
      let state = memoryState;

      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored) state = JSON.parse(stored);
      } catch (_) {}

      const isCurrent =
        state?.formId === this.currentFormId &&
        state?.offerVersion === this.currentOfferVersion &&
        state?.status === SUBMITTED_STATUS &&
        Number(state?.expiresAt) > Date.now();

      return isCurrent ? state : null;
    }

    writeSubmittedState() {
      if (this.readState()) return;

      const submittedAt = Date.now();
      memoryState = {
        formId: this.currentFormId,
        offerVersion: this.currentOfferVersion,
        status: SUBMITTED_STATUS,
        submittedAt,
        expiresAt: submittedAt + STATE_TTL,
      };

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryState));
      } catch (_) {}

      window.dispatchEvent(new Event(STATE_EVENT));
    }

    syncState() {
      const submitted = Boolean(this.readState());
      const label = submitted
        ? this.dataset.submittedLabel
        : this.activeDiscount ? this.dataset.activeLabel : this.dataset.initialLabel;
      const heading = this.activeDiscount
        ? this.dataset.activeHeading
        : this.dataset.submittedHeading;
      const message = this.activeDiscount
        ? this.dataset.activeMessage
        : this.dataset.submittedMessage;
      this.submitted = submitted;
      this.classList.toggle("is-submitted", submitted);
      this.classList.toggle("is-active-discount", this.activeDiscount);
      if (this.trigger && label) this.trigger.setAttribute("aria-label", label);
      if (this.tooltip && label) this.tooltip.textContent = label;
      if (this.dialog && heading) this.dialog.setAttribute("aria-label", heading);
      if (this.dialogTitle && heading) this.dialogTitle.textContent = heading;
      if (this.dialogMessage && message) this.dialogMessage.textContent = message;
      if (this.badge && !this.badge.dataset.initialText) this.badge.dataset.initialText = this.badge.textContent;
      if (this.badge) {
        this.badge.hidden = submitted && !this.activeDiscount;
        this.badge.textContent = this.activeDiscount
          ? this.dataset.activeBadge
          : this.badge.dataset.initialText;
      }
    }

    handleTriggerClick() {
      if (this.submitted) {
        this.openSubmittedDialog();
        return;
      }
      if (this.opening || this.formOpen) return;
      this.openForm(this.currentFormId);
    }

    openForm(formId) {
      if (!formId || this.opening || this.formOpen) return;

      activeOffer = this;
      this.activeFormId = formId;
      this.opening = true;
      this.setTriggerBusy(true);

      const handleTriggered = () => {
        if (activeOffer !== this || this.activeFormId !== formId) return;
        this.clearOpenTimeout();
        this.opening = false;
        this.formOpen = true;
        this.setTriggerBusy(false);
      };

      this.openTimer = window.setTimeout(() => {
        if (!this.opening) return;
        this.opening = false;
        this.activeFormId = null;
        activeOffer = null;
        this.setTriggerBusy(false);
      }, OPEN_TIMEOUT);

      if (typeof window.klaviyo?.openForm === "function") {
        window.klaviyo.openForm(formId, handleTriggered);
      } else {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(["openForm", formId, handleTriggered]);
      }
    }

    handleKlaviyoEvent(event) {
      const detail = event.detail || {};
      if (detail.type === "submit" && detail.formId === this.currentFormId) {
        this.writeSubmittedState();
        this.syncState();
        return;
      }

      if (activeOffer !== this || detail.formId !== this.activeFormId) return;

      if (detail.type === "open") {
        this.clearOpenTimeout();
        this.opening = false;
        this.formOpen = true;
        this.setTriggerBusy(false);
      }

      if (detail.type === "close") {
        this.clearOpenTimeout();
        this.opening = false;
        this.formOpen = false;
        this.activeFormId = null;
        activeOffer = null;
        this.setTriggerBusy(false);
        this.shakeBadge();
        this.trigger?.focus();
      }
    }

    shakeBadge() {
      if (this.activeDiscount || this.submitted || !this.badge) return;
      this.classList.toggle("is-shaking", false);
      this.classList.toggle("is-shaking", true);
    }

    handleBadgeAnimationEnd(event) {
      if (event.animationName === "m-newsletter-offer-shake") {
        this.classList.toggle("is-shaking", false);
      }
    }

    openSubmittedDialog() {
      if (!this.dialog || this.dialog.open || typeof this.dialog.showModal !== "function") return;
      this.dialog.showModal();
      this.dialog.focus({ preventScroll: true });
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.dialog.close();
    }

    handleDialogCloseClick() {
      this.dialog?.close();
    }

    handleDialogClose() {
      this.trigger?.focus();
    }

    handleStateChange() {
      this.syncState();
    }

    handleOrderDiscount(event) {
      const discount = event.detail || {};
      this.activeDiscount = Boolean(
        discount.active && Number(discount.percentage) === 10
      );
      this.syncState();
    }

    handleStorage(event) {
      if (event.key === STORAGE_KEY) this.syncState();
    }

    setTriggerBusy(busy) {
      if (!this.trigger) return;
      this.trigger.disabled = busy;
      this.trigger.setAttribute("aria-busy", busy ? "true" : "false");
    }

    clearOpenTimeout() {
      if (!this.openTimer) return;
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  customElements.define("m-newsletter-offer", MNewsletterOffer);
})();
